// Общото за работния лист „Прехвърляне": типът на реда, FIFO цената, редовете
// на „Целия списък" и класовете на решетката. Прехвърлянето взима стока от ЕДИН
// склад и я слага в друг — количеството не може да надхвърли наличното в
// източника КЪМ ДАТАТА, а себестойността идва от партидите (FIFO или избрана
// партида) и е ВИНАГИ нето. Числовите формати са общите от `components/numbers`.
import type { TFunction } from "i18next";
import type { Lot, Product, StockBalance } from "../../../../api/inventory";
import type { CatalogItem } from "../../components/search/CatalogPickerModal";
import { catalogStockTone } from "../../components/search/catalogStock";
import { formatQtyInput, formatUnitPrice } from "../../components/numbers";
import { clampDecimals } from "../../components/qty";
import { pickableProducts } from "../../components/product/deletedProducts";

// Един ред от работния лист
export type TransferLineDraft = {
  productId: number;
  // количеството е СТРИНГ, за да приема и точка, и запетая, докато се пише
  quantity: string;
  lotId: number | null; // null = FIFO
};

export const emptyTransferLine = (productId: number): TransferLineDraft => ({
  productId,
  quantity: "",
  lotId: null,
});

// FIFO себестойност за дадено количество: претеглена по количествата, взети от
// партидите по реда им. Без количество — цената на първата налична партида.
// При избрана партида — първо тя, остатъкът по FIFO от другите (BE
// `replay._consumption_order`).
export const fifoUnitPrice = (
  lots: { id: number; remaining: number; unitPrice: number }[],
  quantity: number,
  lotId: number | null = null,
): number | null => {
  const chosenLot = lotId ? lots.find((lot) => lot.id === lotId) : undefined;
  const orderedLots = chosenLot
    ? [chosenLot, ...lots.filter((lot) => lot.id !== chosenLot.id)]
    : lots;
  if (orderedLots.length === 0) return null;
  if (quantity <= 0) return orderedLots[0].unitPrice;
  let remainingNeed = quantity;
  let cost = 0;
  let consumed = 0;
  for (const lot of orderedLots) {
    if (remainingNeed <= 0) break;
    const taken = Math.min(lot.remaining, remainingNeed);
    cost += taken * lot.unitPrice;
    consumed += taken;
    remainingNeed -= taken;
  }
  return consumed > 0 ? cost / consumed : orderedLots[0].unitPrice;
};

// „Целият списък": САМО продуктите с наличност в склада-източник към датата
// (прехвърля се само налична стока), без изтритите, в колони — единица ·
// наличност · средна цена (себестойността, по която се прехвърля). Полето
// „Количество" в реда е в базовата мярка; `quantityInSheet` дава написаното
// количество на вече добавения ред (попълненият се презаписва след въпрос).
export const sourceCatalogItemsOf = (
  products: Product[],
  sourceBalanceByProduct: Map<number, StockBalance>,
  addedProductIds: Set<number>,
  t: TFunction,
  quantityInSheet?: (productId: number) => string,
): CatalogItem[] =>
  pickableProducts(products)
    .filter((product) => (sourceBalanceByProduct.get(product.id)?.quantity ?? 0) > 0)
    .sort((a, b) => a.name.localeCompare(b.name, "bg"))
    .map((product) => {
      const balance = sourceBalanceByProduct.get(product.id);
      const quantity = balance?.quantity ?? 0;
      return {
        id: product.id,
        name: product.name,
        group: product.category ?? null,
        unit: product.unit ?? undefined,
        stock: formatQtyInput(quantity, clampDecimals(product.decimalPlaces)),
        stockTone: catalogStockTone(quantity, product.minQuantity),
        price: balance?.weightedAvgPrice != null ? formatUnitPrice(balance.weightedAvgPrice) : undefined,
        meta: [
          product.unit,
          `${t("inventoryPage.productOptStock")} ${formatQtyInput(
            balance?.quantity ?? 0,
            clampDecimals(product.decimalPlaces),
          )}`,
          balance?.weightedAvgPrice != null
            ? `${t("inventoryPage.colUnitPrice")} ${formatUnitPrice(balance.weightedAvgPrice)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · "),
        added: addedProductIds.has(product.id),
        entryUnit: product.unit ?? undefined,
        filledValue: (addedProductIds.has(product.id) && quantityInSheet?.(product.id).trim()) || undefined,
      };
    });

// „Партида 12 · ост. 8,000 кг · срок 12.09.26" — както при изписване
export const lotLabel = (
  lot: Lot,
  decimals: number,
  unit: string | null,
  t: (key: string) => string,
): string =>
  [
    lot.batchNumber
      ? `${t("inventoryPage.batchShort")} ${lot.batchNumber}`
      : t("inventoryPage.noBatch"),
    `${t("inventoryPage.remainShort")} ${formatQtyInput(lot.remaining, decimals)}${unit ? ` ${unit}` : ""}`,
    lot.expiryDate
      ? `${t("inventoryPage.expiryShort")} ${new Date(lot.expiryDate).toLocaleDateString(
          "bg-BG",
          { day: "2-digit", month: "2-digit", year: "2-digit" },
        )}`
      : t("inventoryPage.noExpiry"),
  ].join(" · ");

// Решетката: # · Продукт · Ед. · Наличност · Количество · Партида · Остава ·
// Става · Ед. цена · Нето · Бруто · ✕
export const LINE_GRID_CLASS =
  "grid grid-cols-[44px_minmax(190px,1.5fr)_58px_120px_120px_150px_110px_110px_92px_96px_96px_40px]";
export const LINE_GRID_MIN_WIDTH_CLASS = "min-w-[1180px]";
// колоните на LINE_GRID_CLASS — за пълнежа над реда „Общо"
export const LINE_GRID_COLUMN_COUNT = 12;

// Плоска клетка като в доставка/изписване: прозрачна рамка, на фокус оранжева.
export const FLAT_CELL_CLASS =
  "w-full rounded-[7px] border border-solid border-transparent bg-transparent px-2 text-right font-[ui-monospace,Menlo,monospace] text-[13px] leading-[normal] text-ink outline-none transition-colors focus:border-brand focus:bg-surface";
export const FLAT_CELL_ERROR_CLASS =
  "!border-[1.5px] !border-solid !border-danger";

// ─── Клавиатурна навигация в решетката ──────────────────────────────────────
// Фокусируеми клетки на ред: 0 Количество · 1 Партида. Id-тата са
// `trx-{продукт}-{колона}`, както ги очаква `components/gridNavigation` — и в
// решетката, и в картите под десктоп ширина.
export const TRANSFER_COL_COUNT = 2;
export const cellId = (productId: number, col: number) =>
  `trx-${productId}-${col}`;
